// File: frontend/src/pages/maintenance/MaintenancePage.tsx

import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";
import { useSiteSettings } from "../../context/SiteSettingsContext";
import { LoginModal } from "../../components/registration/LoginModal";
import "./MaintenancePage.css";

/**
 * Route that stays usable while maintenance mode is on, so a locked-out admin
 * has a way back in. Every other path renders the plain notice below.
 * Deliberately not linked from anywhere on the page: a normal user cannot get
 * in during maintenance, so offering them a login form is pointless noise.
 */
const ADMIN_LOGIN_PATH = "/login";

/**
 * Full-screen maintenance page shown to non-admin users when maintenance mode is active.
 * Admins see the normal site regardless of this setting.
 *
 * The gate in App.tsx runs before the router, so while maintenance is on every
 * route lands here for non-admins. Two consequences are handled below:
 *  - any signed-in non-admin session is terminated on arrival and told why;
 *  - /login still renders a login form, which is how the admin gets back in
 *    (signing in as ADMIN makes the gate fall through and the site reappears).
 */
export const MaintenancePage: React.FC = () => {
    const { t } = useTranslation();
    const { siteName } = useSiteSettings();
    const { logout, isAuthenticated } = useAuth();
    const location = useLocation();

    const isAdminLoginRoute = location.pathname === ADMIN_LOGIN_PATH;

    // The login form is open by default on the admin login route; closing it
    // leaves the button below to reopen it.
    const [isLoginOpen, setIsLoginOpen] = useState(isAdminLoginRoute);
    // Set once a session was actually terminated here, so the notice is only
    // shown to someone who really was signed in a moment ago.
    const [wasSignedOut, setWasSignedOut] = useState(false);
    const signOutDoneRef = useRef(false);

    // Terminate any signed-in non-admin session. This component only renders for
    // non-admins (admins never hit the gate), so reaching it means the session is
    // not allowed in while maintenance is on — end it instead of leaving a
    // half-usable logged-in state behind, and say so on screen.
    useEffect(() => {
        if (isAuthenticated && !signOutDoneRef.current) {
            signOutDoneRef.current = true;
            logout();
            setWasSignedOut(true);
        }
    }, [isAuthenticated, logout]);

    return (
        <div className="maintenance-page">
            <div className="maintenance-card">
                {/* Wrench icon */}
                <div className="maintenance-icon">🔧</div>

                <h1 className="maintenance-title">{siteName}</h1>
                <h2 className="maintenance-heading">{t("maintenance_heading", "Bakım Çalışması")}</h2>
                <p className="maintenance-text">
                    {t(
                        "maintenance_text",
                        "Siteye daha iyi hizmet verebilmek için bakım çalışması yapılmaktadır. Kısa süre içinde geri döneceğiz."
                    )}
                </p>

                {wasSignedOut && (
                    <p className="maintenance-notice">
                        {t("maintenance_signed_out", "Bakım çalışması nedeniyle oturumunuz kapatıldı.")}
                    </p>
                )}

                {/* Admin login route only — nothing clickable on the notice itself */}
                {isAdminLoginRoute && (
                    <>
                        {!isLoginOpen && (
                            <div className="maintenance-actions">
                                <button
                                    className="maintenance-login-btn"
                                    onClick={() => setIsLoginOpen(true)}
                                >
                                    {t("maintenance_admin_login", "Yönetici Girişi")}
                                </button>
                            </div>
                        )}
                        <p className="maintenance-admin-hint">
                            {t(
                                "maintenance_admin_hint",
                                "Bakım modu açıkken yalnızca yöneticiler giriş yapabilir. Giriş yaptıktan sonra Admin → Ayarlar → Genel → Bakım Modu üzerinden kapatabilirsiniz."
                            )}
                        </p>
                    </>
                )}
            </div>

            {isAdminLoginRoute && (
                <LoginModal
                    isOpen={isLoginOpen}
                    onClose={() => setIsLoginOpen(false)}
                />
            )}
        </div>
    );
};